export class TemplateRenderer {
  private template: string;
  private insertMethod: string;
  private elementId: string;
  private data: any;

  constructor(
    template: string,
    data: any,
    elementId: string,
    insertMethod: string = 'innerHTML',
  ) {
    this.template = template;
    this.elementId = elementId;
    this.data = data;
    this.insertMethod = insertMethod;
  }

  private getValue(obj: any, path: string): any {
    return path
      .split('.')
      .reduce((o, i) => (o && o[i] !== 'undefined' ? o[i] : undefined), obj);
  }

  private interpolate(match: string, key: string): string {
    const value = this.getValue(this.data, key);
    return value !== undefined ? value.toString() : '';
  }

  //NOTE: Keeping this here for reference if i decide to add ! logic back in after changing to tokenize method (i would need to change how I am grouping them since I'm relying on the name being the same accross all ifs/else/endif)
  private handleIf(
    match: string,
    condition: string,
    content: string,
    elseContent: string = '',
  ): string {
    //console.log('Evaluating condition:', condition);
    let shouldRender = this.getValue(this.data, condition);

    if (condition.trim().startsWith('!')) {
      shouldRender = !shouldRender;
      condition = condition.trim().slice(1); // Remove the ! for getValue
    }

    //console.log('Should render:', shouldRender);
    return shouldRender ? content : elseContent;
  }

  private tokenize(template: string): Array<{
    type: string;
    condition: string;
    content: string;
    index: number;
  }> {
    //const tokenRegex = /{% (if|else|endif)(?: (.+?))? %}/g;
    const tokenRegex = /{(#if|else|\/if)?\s*([\w.]*)}|([^{}]+)/g;

    const tokens: Array<{
      type: string;
      condition: string;
      content: string;
      index: number;
    }> = [];
    let match;
    let i = -1;
    while ((match = tokenRegex.exec(template)) !== null) {
      i++;
      if (match[1]) {
        tokens.push({
          type: match[1],
          condition: match[2],
          content: '',
          index: i,
        });
      } else if (match[3]) {
        tokens.push({
          type: 'text',
          condition: '',
          content: match[3],
          index: i,
        });
      }
    }
    //console.log("Tokens:", tokens); // Debugging tokens
    return tokens;
  }

  private addSpreadIntoArray(start: number, end: number): number[] {
    let arr = [];
    for (let i = start; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  }

  private processTokens(
    tokens: Array<{
      type: string;
      condition: string;
      content: string;
      index: number;
    }>,
  ): string {
    const stack: Array<{
      type: string;
      condition: string;
      content: string;
      index: number;
    }> = [];
    const output: string[] = [];

    //console.log("Tokens:", tokens); // Debugging tokens

    const groupedItems = tokens.reduce((acc, item) => {
      const { condition } = item;
      if (!acc[condition]) {
        acc[condition] = [];
      }
      acc[condition].push(item);
      return acc;
    }, {});

    //console.log("Grouped items:", groupedItems);

    const tokenLength = tokens.length;

    let processedIndexes: number[] = [];
    let indexesToRender: number[] = [];
    let contentToRender = [];
    for (let i = 0; i < tokens.length; i++) {
      const element = tokens[i];
      //console.log("element:", element);
      if (processedIndexes.includes(element.index)) {
        //console.log("Skipping element:", element);
        continue;
      }
      if (element.type === 'text') {
        //console.log("is text:", element, "adding to processedIndexes", element.index);
        processedIndexes.push(element.index);
        indexesToRender.push(element.index);
        contentToRender.push(element.content);
      }
      if (element.type === '#if') {
        //is if, so get condition
        processedIndexes.push(element.index);

        const condition = element.condition;
        const passed = this.getValue(this.data, condition);
        const elseCondition = groupedItems[condition].find(
          (token) => token.type === 'else',
        );
        const endIfCondition = groupedItems[condition].find(
          (token) => token.type === '/if',
        );

        //console.log("condition:", condition, "passed:", passed, "elseCondition:", elseCondition, "endIfCondition:", endIfCondition);

        if (passed && elseCondition) {
          //console.log("Passed and has else:", condition, "elseCondition:", elseCondition, " ---- should skip to endif index", endIfCondition.index);
          const ar = this.addSpreadIntoArray(
            elseCondition.index,
            endIfCondition.index,
          );
          //console.log("XXXXXX ar:", ar);
          processedIndexes = processedIndexes.concat(ar);

          //we passed this check
        }
        if (!passed && elseCondition) {
          //console.log("Passed and has else:", condition, "elseCondition:", elseCondition, " ---- should skip to endif index", endIfCondition.index);
          const ar = this.addSpreadIntoArray(
            element.index,
            elseCondition.index,
          );
          //console.log("XXXXXX ar:", ar);
          processedIndexes = processedIndexes.concat(ar);

          //we passed this check
        }
      }
    }
    //console.log(`Processed indexes => ${processedIndexes} === Indexes to render => ${indexesToRender} === Content to render => ${contentToRender}}` );
    //console.log("Processed indexes:", processedIndexes, "Indexes to render:", indexesToRender, "Content to render:", contentToRender);

    return contentToRender.join('');
  }

  private render() {
    let result = this.template.replace(
      /{{(.*?)}}/g,
      this.interpolate.bind(this),
    );
    const tokens = this.tokenize(result);
    //console.log('Tokens:', tokens);
    const res = this.processTokens(tokens);
    //console.log('Result:', res);
    //let element;
    const element = document.querySelector(this.elementId) as HTMLElement;
    //console.log("element:", element);
    const isMobileTopNav = document.querySelector(
      'header .absolute.right-0 .relative',
    );
    if (this.insertMethod === 'after') {
      const newDiv = document.createElement('div');
      newDiv.id = 'mng-user-profile';
      newDiv.innerHTML = res;
      element.insertAdjacentHTML('afterend', newDiv.outerHTML);
      this.elementId = '#mng-user-profile';
      this.insertMethod = 'innerHTML';
      if (isMobileTopNav) {
        isMobileTopNav.insertAdjacentHTML('afterend', newDiv.outerHTML);
      }
    } else {
      // Dual inject (bottom nav + mobile top nav) creates multiple #mng-user-profile
      const profiles = document.querySelectorAll('#mng-user-profile');
      if (profiles.length > 0) {
        profiles.forEach((profile) => {
          profile.innerHTML = res;
        });
      } else if (element) {
        element.innerHTML = res;
      }
    }

    // if (element) {
    //     if(this.insertMethod === 'innerHTML'){

    //         element.innerHTML = res;
    //     }else if(this.insertMethod === 'after'){
    //         element.insertAdjacentHTML('afterend', res);
    //     }
    // }
  }

  public bindData(): any {
    const self = this;
    const proxy = new Proxy(this.data, {
      get(target, prop, receiver) {
        //console.log("Proxy.get", "target", target, "prop", prop, "receiver", receiver);
        if (typeof target[prop] === 'object' && target[prop] !== null) {
          return self.bindData.call(self, target[prop]);
        }
        return Reflect.get(target, prop, receiver);
      },
      set(target, prop, value, receiver) {
        //console.log("Proxy.set", "target", target, "prop", prop, "value", value, "receiver", receiver);
        const result = Reflect.set(target, prop, value, receiver);
        self.render(); // Trigger re-render on any set operation
        return result;
      },
    });

    this.render();
    return proxy;
  }
}
